import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import type { OperatorBranding } from "../shared/api.ts";
import { consoleAccentIsLegible, type WhiteLabelChrome } from "../shared/white-label-chrome.tsx";
import { BrandMark } from "./BrandMark.tsx";

/** Also the display_name a tenant carries until they white-label the
 *  console -- see DEFAULT_OPERATOR_BRANDING in appearance-context.tsx. */
const PRODUCT_NAME = "Satatam";

/** The console's shipped pair. Also the fallback for an accent the console
 *  legibility gate refuses -- see the mark accent below. */
const PRODUCT_ACCENT = "#3FCF8E";
const PRODUCT_ON_ACCENT = "#001A0B";

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
const RASTER_DATA_URL = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

function safeHex(value: unknown, fallback: string): string {
  return typeof value === "string" && HEX_COLOR.test(value) ? value : fallback;
}

function safeRasterDataUrl(value: unknown): string | null {
  return typeof value === "string"
    && value.length <= 87_500
    && RASTER_DATA_URL.test(value)
    ? value
    : null;
}

function initialsFor(displayName: string): string {
  return displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => (Array.from(word)[0] ?? "").toUpperCase())
    .join("");
}

export function OperatorBrand({
  branding,
  chrome,
}: {
  branding: OperatorBranding;
  /** Published white-label policy. Absent means an un-white-labelled
   *  console, which is also what every caller outside the shell wants. */
  chrome?: WhiteLabelChrome;
}) {
  const [logoState, setLogoState] = useState<{
    src: string | null;
    status: "idle" | "loading" | "ready" | "failed";
  }>({ src: null, status: "idle" });
  const eyeSlot = useRef<HTMLSpanElement | null>(null);
  const rawDisplayName = typeof branding.display_name === "string"
    ? branding.display_name.trim()
    : "";
  const displayName = rawDisplayName || PRODUCT_NAME;
  const safeLogo = safeRasterDataUrl(branding.logo_data_url);
  const initials = useMemo(() => initialsFor(displayName), [displayName]);
  // A tenant who uploaded a logo but turned "use logo as sidebar mark" off
  // keeps the logo on their widget and their initials in the console.
  const logoPresent = Boolean(safeLogo);
  const logoAllowed = !chrome?.companyName || chrome.useLogoAsMark;
  const logoStatus = logoState.src === safeLogo ? logoState.status : "idle";
  const showLogo = logoPresent && logoAllowed && logoStatus === "ready";
  /* The product's own mark is the default whenever there is no tenant logo. A
     tenant name alone no longer replaces it: the name is already written
     beside the mark, so an initialism of it adds nothing, and the eye is what
     the console is recognised by.
     Initials stand in for exactly one thing -- a tenant logo that exists and
     cannot be drawn, because it was turned off for this slot or because it
     failed to load. Falling back to the product's own mark there would
     replace a tenant's identity with ours at the moment their asset broke. */
  const showEye = !showLogo && !logoPresent;
  /* The mark is chrome like any other, so it answers to the same gate the rest
     of the console does. `consoleAccentIsLegible` is 3:1 dual-ground against
     `--bg` in both themes; an accent that fails it is one the operator cannot
     read as console furniture, and a 32px block of it beside the workspace
     name is the least readable place it could land. Rejected falls back to the
     product pair -- the same thing the rest of the console keeps -- rather
     than to the tenant's initials: their asset has not broken, their colour
     has been refused, and those are different states with different marks.
     Both tokens fall back together: an `on_accent` chosen against a rejected
     accent is a contrast pairing with nothing on the other side. */
  const requestedAccent = safeHex(branding.accent, PRODUCT_ACCENT);
  const markAccentIsLegible = consoleAccentIsLegible(requestedAccent);
  const markAccent = markAccentIsLegible ? requestedAccent : PRODUCT_ACCENT;
  const markOnAccent = markAccentIsLegible
    ? safeHex(branding.on_accent, PRODUCT_ON_ACCENT)
    : PRODUCT_ON_ACCENT;
  const markClass = [
    "operator-brand-mark",
    showEye ? "is-eye" : "",
    showLogo ? "has-logo" : "",
  ].filter(Boolean).join(" ");

  useEffect(() => {
    if (!safeLogo || !logoAllowed || typeof window === "undefined") {
      setLogoState({ src: safeLogo, status: "idle" });
      return;
    }

    let active = true;
    setLogoState({ src: safeLogo, status: "loading" });
    const image = new window.Image();
    image.onload = () => {
      if (active) setLogoState({ src: safeLogo, status: "ready" });
    };
    image.onerror = () => {
      if (active) setLogoState({ src: safeLogo, status: "failed" });
    };
    image.src = safeLogo;

    return () => {
      active = false;
    };
  }, [logoAllowed, safeLogo]);

  /* The product mark is a shaded three.js sphere on the marketing site, and the
     SVG below is a hand-built approximation of it. Side by side they read as
     two different logos, so draw the real one here whenever we can and let the
     SVG be what stays on screen when we cannot -- no WebGL, or the chunk fails
     to load. three.js is ~150KB gzipped and only an un-white-labelled console
     ever draws this, so it loads after first paint rather than in the entry
     chunk. */
  useEffect(() => {
    const host = eyeSlot.current;
    if (!showEye || !host) return;
    let dispose: (() => void) | null = null;
    let canvas: HTMLCanvasElement | null = null;
    let onContextLost: (() => void) | null = null;
    let cancelled = false;
    void import("./brandEye.js")
      .then(({ mountBrandEye }) => {
        if (cancelled) return;
        dispose = mountBrandEye(host);
        canvas = host.querySelector("canvas");
        if (!dispose || !canvas) return;

        const release = dispose;
        onContextLost = () => {
          /* The SVG is hidden only while this canvas is live. Browsers can
             evict a WebGL context under pressure; tear it down so the product
             mark comes back instead of leaving a blank slot in the sidebar. */
          const lostCanvas = canvas;
          if (lostCanvas && onContextLost) {
            lostCanvas.removeEventListener("webglcontextlost", onContextLost);
          }
          onContextLost = null;
          dispose = null;
          release();
        };
        canvas.addEventListener("webglcontextlost", onContextLost, { once: true });
      })
      .catch(() => { /* the SVG mark stays exactly as it is */ });
    return () => {
      cancelled = true;
      if (canvas && onContextLost) {
        canvas.removeEventListener("webglcontextlost", onContextLost);
      }
      if (dispose) dispose();
    };
  }, [showEye]);

  return (
    <span
      className="operator-brand"
      style={{
        "--tenant-accent": markAccent,
        "--tenant-on-accent": markOnAccent,
      } as CSSProperties}
    >
      <span className={markClass} aria-hidden="true">
        {showLogo ? (
          <img
            className="tenant-logo"
            src={safeLogo!}
            alt=""
            onLoad={() => setLogoState({ src: safeLogo, status: "ready" })}
            onError={(event) => {
              // Hide the native broken-image glyph before React paints the
              // initials fallback. This keeps a bad tenant asset from ever
              // escaping the 32px mark slot or flashing at page origin.
              event.currentTarget.hidden = true;
              setLogoState({ src: safeLogo, status: "failed" });
            }}
          />
        ) : showEye ? (
          /* The product's own mark. This is `BrandMark`, not `BrandOrb`:
             BrandMark is the marketing site's `.logo-mark` geometry and
             animation exactly, down to the class names base.css keys its
             keyframes off -- including `.app-brand:hover .logo-mark`, written
             for this very slot. BrandOrb is the widget's variant and reads as
             a different mark beside the marketing site. One brand, one mark. */
          <>
            {/* Ordered slot-then-mark on purpose: the stylesheet hides the SVG
                with `.eye-slot.live ~ .mark`, and `~` only reaches forward. */}
            <span className="eye-slot" ref={eyeSlot} />
            <BrandMark className="mark" size={32} />
          </>
        ) : (
          <span className="operator-brand-initials">{initials}</span>
        )}
      </span>
      <span className="operator-brand-copy">
        <span className="operator-brand-name">{displayName}</span>
        {/* Concealing the product name leaves this line to the tenant's own
            workspace label -- dropping it entirely would strand the brand
            name on a single line and change the block's height. */}
        <span className="operator-brand-attribution">
          {chrome?.replaceProductName
            ? chrome.workspaceLabel
            : "Satatam · Operator"}
        </span>
      </span>
    </span>
  );
}
